import { memo, useCallback, useId, useState } from 'react';

import { Box, Menu, MenuItem, Typography } from '@mui/material';

import { Iconify } from 'src/components/iconify';

import {
  stepHeaderMenuItemSx,
  stepHeaderPillBadgeSx,
  stepHeaderPillLabelSx,
  stepHeaderPillSx,
} from './step-header-pill-styles';

export interface StepHeaderDropdownOption {
  value: string;
  label: string;
}

interface StepHeaderDropdownPillProps {
  badge: number | string;
  label: string;
  options: StepHeaderDropdownOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  selected?: boolean;
}

export const StepHeaderDropdownPill = memo(
  ({
    badge,
    label,
    options,
    value,
    defaultValue,
    onChange,
    selected = false,
  }: StepHeaderDropdownPillProps) => {
    const menuId = useId();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [internalValue, setInternalValue] = useState(defaultValue ?? options[0]?.value ?? '');

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;
    const isOpen = Boolean(anchorEl);

    const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleSelect = useCallback(
      (nextValue: string) => {
        if (!isControlled) {
          setInternalValue(nextValue);
        }
        onChange?.(nextValue);
        handleClose();
      },
      [handleClose, isControlled, onChange]
    );

    return (
      <>
        <Box
          component="button"
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? menuId : undefined}
          onClick={handleOpen}
          sx={{
            ...stepHeaderPillSx(selected || isOpen),
            cursor: 'pointer',
            m: 0,
            p: 0,
            font: 'inherit',
            textAlign: 'left',
            appearance: 'none',
            WebkitAppearance: 'none',
          }}
        >
          <Box component="span" sx={stepHeaderPillBadgeSx}>
            {badge}
          </Box>
          <Typography component="span" sx={stepHeaderPillLabelSx}>
            {label}
          </Typography>
          <Iconify icon="eva:arrow-ios-downward-fill" width={24} sx={{ color: 'text.primary' }} />
        </Box>

        <Menu
          id={menuId}
          anchorEl={anchorEl}
          open={isOpen}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                minWidth: anchorEl?.offsetWidth ?? 160,
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === currentValue}
              onClick={() => handleSelect(option.value)}
              sx={stepHeaderMenuItemSx}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }
);

StepHeaderDropdownPill.displayName = 'StepHeaderDropdownPill';
